// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { DataOption, DataOptions, getDataOptionValue } from "@toolkit/Framework/Common/Utilities/dataOptions";
import React, { FC, forwardRef, useMemo } from "react";
import styled from "styled-components";

export type VideoPreloadValue = "metadata" | "auto" | "none";
export type VideoPreloadOption = DataOption<VideoPreloadValue>;
export interface VideoPreloadOptionMapper {
    auto: VideoPreloadOption;
    metadata: VideoPreloadOption;
    none: VideoPreloadOption;
}
export const videoPreloadOptions: VideoPreloadOptions = {
    auto: { id: "auto", displayName: "auto", value: "auto", isDefault: true },
    metadata: { id: "metadata", displayName: "metadata", value: "metadata" },
    none: { id: "none", displayName: "none", value: "none" },
};

export type VideoPreloadOptions = DataOptions<VideoPreloadOption> & VideoPreloadOptionMapper;

export type VideoTypeValue = "video/mp4" | "video/webm" | "video/ogg";
export type VideoTypeOption = DataOption<VideoTypeValue>;
export interface VideoTypeOptionMapper {
    mp4: VideoTypeOption;
    webm: VideoTypeOption;
    ogg: VideoTypeOption;
}
export const videoTypeOptions: VideoTypeOptions = {
    mp4: { id: "mp4", displayName: "video/mp4", value: "video/mp4", isDefault: true },
    webm: { id: "webm", displayName: "video/webm", value: "video/webm" },
    ogg: { id: "ogg", displayName: "video/ogg", value: "video/ogg" },
};

export type VideoTypeOptions = DataOptions<VideoTypeOption> & VideoTypeOptionMapper;

export interface VideoProps extends VideoPresentationProps {
    /** Specifies that the video will start playing as soon as it is ready */
    autoPlay?: boolean;
    /** Specifies that video controls should be displayed (such as a play/pause button etc). */
    controls?: boolean;
    /** Sets the height of the video player */
    height?: number | string;
    /** Specifies if the video is coming from an external source like YouTube or Vimeo, etc. */
    isEmbededIframe?: boolean;
    /** Specifies that the video will start over again, every time it is finished */
    loop?: boolean;
    /** Specifies that the audio output of the video should be muted */
    muted?: boolean;
    /** Specifies an image to be shown while the video is downloading, or until the user hits the play button */
    poster?: string;
    /** Specifies if and how the author thinks the video should be loaded when the page loads */
    /** * auto: Downloading the entire video right away is considered desirable. An empty string produces the same result. */
    /** * metadata: Metadata (duration, dimensions, text tracks) should be preloaded, but with minimal video. */
    /** * none: The user might chose not to watch the video, so don't preload anything. */
    preload?: VideoPreloadValue;
    /** Specifies the URL of the video file */
    src: string;
    /** Specifies the media type */
    type?: VideoTypeValue;
    /** Sets the width of the video player */
    width?: number | string;
}

export interface VideoPresentationProps extends ExpandStyles<VideoStyles> {}

export interface VideoStyles {
    video?: InjectableCss;
    iframe?: InjectableCss;
}

export const videoStyles: VideoStyles = {};

export const StyledVideo = styled.video`
    ${injectCss}
`;

export const StyledIframe = styled.iframe`
    ${injectCss}
`;

const Video: FC<VideoProps> = forwardRef<HTMLVideoElement, VideoProps>((props, ref) => {
    const {
        extendedStyles,
        overridedStyles,
        autoPlay = false,
        controls = true,
        height = "auto",
        isEmbededIframe = false,
        loop = false,
        muted = true,
        poster,
        preload = "auto",
        src,
        type = "video/mp4",
        width = "100%",
        ...htmlAttributes
    } = props;

    const styles = useExpandProps(videoStyles, extendedStyles, overridedStyles);
    const typeData = useMemo(() => getDataOptionValue(videoTypeOptions, type), [type]);
    const preloadData = useMemo(() => getDataOptionValue(videoPreloadOptions, preload), [preload]);

    if (!src) {
        return null;
    }

    return !isEmbededIframe ? (
        <StyledVideo
            {...styles.video}
            {...htmlAttributes}
            autoPlay={autoPlay}
            controls={controls}
            height={height}
            loop={loop}
            muted={muted}
            poster={poster}
            preload={preloadData}
            ref={ref}
            width={width}
        >
            <source src={src} type={typeData} />
            {translate("Your browser doesn't support HTML5 videos.")}
        </StyledVideo>
    ) : (
        <StyledIframe
            {...styles.iframe}
            src={src}
            frameBorder="0"
            title={translate("Video player")}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            width={width}
            height={height}
        />
    );
});

export default Video;
